/**
 * Site Settings V2 — "Billing" section (staff-only): the alternate
 * bill-to site-group target, parity with the legacy "Site Billing"
 * menu option / SiteBillingDialog.
 *
 * Contains:
 * - BillingSection (default export)
 */

import PaidIcon from '@mui/icons-material/Paid';
import { useField } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import SiteGroupAutocomplete from '@sophocles/components/SiteGroupAutocomplete';
import StatusChip from '@sophocles/components/StatusChip';
import { selectCustomer } from '@sophocles/features/auth/authSelectors';
import { SettingsFieldRow, SettingsSection, SettingsSubsection } from '@sophocles/views/settings/components';

// Live billing section — currently surfaces only the alternate
// site-group target (parity with the legacy "Site Billing" menu
// option / SiteBillingDialog).  The bigger draft layout below
// stays parked until the SiteBilling field-type cleanup lands.
export default function BillingSection({ customerId }) {
  const { t } = useTranslation();
  const customer = useSelector(selectCustomer(customerId));
  const isBillboard = customer?.is_billboard ?? false;
  const siteGroupTerm = t('siteGroups.siteGroup');
  const [field, , helpers] = useField('billing.bill_to_alternate_site_group_id');
  return (
    <SettingsSection
      id="settings-billing"
      icon={<PaidIcon />}
      title={t('siteSettings.v2.billing', 'Billing')}
      description={t(
        'siteSettings.v2.billingDescription',
        'Bill the unit to a {{groupTerm}} other than its default. Leave blank to bill the primary group.',
        { groupTerm: siteGroupTerm.toLowerCase() },
      )}
      chip={<StatusChip color="info" label={t('siteSettings.staffOnly', 'Staff Only')} />}
    >
      <SettingsSubsection title={t('siteSettings.v2.billingTarget', 'Bill-to Target')} bordered>
        <SettingsFieldRow
          label={t('siteSettings.v2.alternateGroup', 'Alternate {{groupTerm}}', { groupTerm: siteGroupTerm })}
          description={t(
            'siteSettings.v2.alternateGroupDescription',
            'When set, billing for this site routes to the chosen {{groupTerm}} instead of the primary one.',
            { groupTerm: siteGroupTerm.toLowerCase() },
          )}
          controlWidth={360}
        >
          <SiteGroupAutocomplete
            fullWidth
            customerId={customerId}
            label={siteGroupTerm}
            value={field.value ?? null}
            onChange={(_e, siteGroupId) => helpers.setValue(siteGroupId)}
            variant="filled"
            hiddenLabel
          />
        </SettingsFieldRow>
      </SettingsSubsection>
    </SettingsSection>
  );
}
